<template>
    <Modal
        id="product-view"
        ref="productModalRef"
        class="product-view h-100 product-modal-padding"
        @click.stop
        @scroll.passive="handleScroll"
        @close="emit('close')">
        <template v-if="images">
            <div class="d-flex justify-content-start p-16 gap-12 product-modal-top-bar" style="margin-top: -54px" :style="toggleBar ? {'background': '#FFFFFF'} : {}">
                <ButtonIcon ariaLabel="Exit button" style="border-radius: 50px; background-color: #F1F0F6" icon="far fa-times" variant="borderless" size="1-5x" @click="toggleModalAnimation('product-view'); emit('close')"/>
                <span v-if="toggleBar" class="tm18 text-midnight-800">{{ props.product.name }}</span>
            </div>
            <div ref="carouselRef" class="position-relative d-flex justify-content-end">
                <ImageCarousel :images="product.images" :parent="carouselRef" :width="productModalRef?.$el.children[0].offsetWidth"/>
            </div>
        </template>
        <template v-else>
            <div class="d-flex justify-content-start p-16">
                <ButtonIcon ariaLabel="Exit button" icon="far fa-times" variant="borderless" size="1-5x" @click="toggleModalAnimation('product-view'); emit('close')"/>
            </div>
        </template>
        <div class="d-flex flex-column p-16">
            <div class="d-flex flex-column gap-12 pb-16">
                <div class="d-flex justify-content-between">
                    <span class="tm18 text-midnight-800">{{ props.product.name }}</span>
                    <div class="d-flex gap-8">
                        <div v-if="props.product.salePrice < props.product.price" class="d-flex gap-8">
                            <span class="text-midnight-700 tm18" style="text-decoration: line-through" v-text="displayWithCurrency(props.product.price)"/>
                            <span class="tb18 text-peach-600" v-text="displayWithCurrency(props.product.salePrice)"/>
                        </div>
                        <span v-else class="text-midnight-800 tb18" v-text="displayWithCurrency(props.product.price)"/>
                    </div>
                </div>
                <div v-if="props.product.buyGetPromotion.enabled" class="d-flex justify-content-start">
                    <Badge
                        :text="buildBogoCatchPhrase(props.product.buyGetPromotion)"
                        :variant="brandColor"/>
                </div>
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="tr16 text-midnight-800 pb-16" style="list-style-position: inside;" v-html="props.product.description"/>
            <template v-if="props.product.productOptions.length > 0">
                <div v-for="(productOption, key) in props.product.productOptions" :key="key" class="product-options pt-24">
                    <RadioList
                        v-model="selectedOptions[productOption.name]"
                        :id="`variant-option-${key}}`"
                        :name="`variant-list-${key}`"
                        :label="productOption?.name ?? ''"
                        position="left"
                        :options="productOption.options.split(',').map(v => ({ text: v.trim(), value: v }))"/>
                </div>
            </template>
            <template v-if="props.product.productAddons.length > 0">
                <div class="d-flex justify-content-between py-16 border-midnight-200">
                    <span class="tm18 text-midnight-800">Add extra</span>
                    <span class="tm16 text-midnight-700">Optional</span>
                </div>
                <div class="product-addons">
                    <div v-for="(productAddon, key) in props.product.productAddons" class=" addon-checkbox d-flex justify-content-between align-items-center py-16" :key="key">
                        <CheckBox
                            @input="v => selectedAddons[productAddon.id] = v"
                            :id="`addon-checkbox-${key}`"
                            class="tr16 text-midnight-600"
                            position="left"
                            :label="productAddon.addon.name"/>
                        <span class="tr16 text-midnight-600" v-text="'+ ' + displayWithCurrency(productAddon.price)"/>
                    </div>
                </div>
            </template>
        </div>
        <template v-if="product" #actions>
            <div class="d-flex flex-column">
                <div class="d-flex justify-content-between py-8">
                    <Button
                        variant="borderless"
                        style="padding: 0"
                        :text="`${notes.length > 0 && !textarea ? 'Edit' : 'Add'} special instructions`"
                        @click="textarea = true"/>
                    <Button v-if="textarea" variant="borderless" style="padding: 0" text="Done" @click="textarea = false"/>
                </div>
                <div v-if="textarea" class="pb-8">
                    <Input
                        id="notes-textarea"
                        v-model="notes"
                        name="textarea-field"
                        type="textarea"
                        label="Have any preference? Add them here (optional)"/>
                </div>
                <div class="d-flex stepper-style gap-12 pt-16 pb-8">
                    <Stepper id="product-stepper" v-model="count" :min="1"/>
                    <Button
                        :disabled="addDisabled || isAddButtonLoading"
                        class="flex-grow-1 h-auto"
                        :text="`Add to Order ${displayWithCurrency(totalPrice)}`"
                        variant="primary"
                        @click="addToOrder"/>
                </div>
            </div>
        </template>
    </Modal>
</template>
<script lang="ts" setup>
import {computed, onMounted, reactive, ref, watch} from 'vue';
import {Input, Badge, Button, Stepper, Modal, ButtonIcon, RadioList, CheckBox} from '@helcim/blizzard';
import Product from '../../store/classes/Product';
import {toggleModalAnimation} from '../../models/utilities/ToggleModalAnimation';
import ImageCarousel from './ImageCarousel.vue';
import {useActors, useSettings, useUtilCurrency, useNotificationToast} from '../../compositions';
import {VARIANT_STATUS} from '../../scripts/constants';
import {buildBogoCatchPhrase, isOutOfStock} from '../../store/utilities';
import {useCartStore} from '../../store/cart';
import {serialize} from 'php-serialize';

const { displayWithCurrency } = useUtilCurrency();
const { emitNotice } = useNotificationToast();

const emit = defineEmits<{
    (event: 'close'): void
}>();

const props = defineProps<{
    product: Product,
}>();

const { brandColor, inventorySetting } = useSettings();

const productModalRef = ref<InstanceType<typeof Modal>>(null);

const images = computed(() => !!props.product?.images?.length);
const textarea = ref(false);
const count = ref(1);
const notes = ref('');
const carouselRef = ref<HTMLDivElement>(null);
const toggleBar = ref(false);
const isAddButtonLoading = ref(false);

const handleScroll = (event: Event) => {
    toggleBar.value = (event.target as HTMLDivElement).scrollTop > 440;
};

const selectedOptions = reactive<Record<string, unknown>>({});
const selectedAddons = reactive<Record<string, unknown>>({});

watch(() => props.product, () => {
    notes.value = '';
    count.value = 1;
    Object.keys(selectedOptions).forEach(key => delete selectedOptions[key]);
    Object.keys(selectedAddons).forEach(key => delete selectedAddons[key]);
    props.product.productOptions.forEach(v => selectedOptions[v.name] = '');
    props.product.productAddons.forEach(v => selectedAddons[v.id] = 0);
});

const computedOptions = computed(() => {
    return Object.entries(selectedOptions).filter(([, v]) => v).map(([k, v]) => {
        return {
            groupName: k,
            selectedValue: v,
        };
    });
});

const computedAddons = computed(() => {
    return Object.entries(selectedAddons).filter(v => v).map(([k, v]) => {
        return {
            name: k,
            value: v,
        };
    });
});

const cartStore = useCartStore();

const addToOrder = async () => {
    isAddButtonLoading.value = true;
    await cartStore.addToCart(props.product, count.value, computedAddons.value, computedOptions.value, notes.value)
        .then(() => {
            toggleModalAnimation('product-view');
            emit('close');
        }).catch(e => {
            emitNotice(0, e, []);
        }).finally(() => {
            isAddButtonLoading.value = false;
            useCartStore().hideOverlay();
        })
};

const addDisabled = computed(() => {
    if(!count.value) { return true; }
    if(isOutOfStock(props.product, inventorySetting.value)) { return true; }
    if(props.product.variantStatus === VARIANT_STATUS.IS_PRODUCT) { return false; }
    return props.product.productOptions.length !== computedOptions.value.length;
});

const isRangedPriced = computed(() =>{
    return !!props.product?.productPricing && props.product.productPriceRanges?.length > 0;
});

function getRangePrice(targetQuantity: number, product: Product): number {
    if(!isRangedPriced.value) {
        return 0;
    }
    const [{
        quantityTo: upperBoundQuantity,
        amount: upperBoundAmount,
    }] = product.productPriceRanges.slice(-1);
    if(upperBoundQuantity <= targetQuantity) { return upperBoundAmount; }

    const targetRangedPrice = product.productPriceRanges.find(rangePrice => {
        return rangePrice.quantityFrom <= targetQuantity && targetQuantity <= rangePrice.quantityTo;
    });
    return targetRangedPrice.amount;
}

const { customer } = useActors();

const isWholesale = computed(() => {
    return customer.value?.wholesale && props.product?.enableWholesale;
});

const totalAddonPrice = computed(() => {
    let price = 0;
    Object.entries(selectedAddons).forEach(([k, v]) => {
        if(v) {
            const addon = props.product.productAddons.filter(addon => addon.id === +k)[0];
            price += isWholesale.value ? addon.wholesalePrice : addon.price;
        }
    });
    return price;
});

function buildOptionsByGroupName(selectedOptions: Record<string, unknown>[]): Record<string, string> {
    const keyedByGroupName: Record<string, string> = {};
    for (const selectedRadioOption of selectedOptions) {
        if(selectedRadioOption.selectedValue === '') { continue; }
        keyedByGroupName[selectedRadioOption.groupName as string] = selectedRadioOption.selectedValue as string;
    }
    return keyedByGroupName;
}

const offering = computed(() => {
    const optionsByGroupName = buildOptionsByGroupName(computedOptions.value);
    const variant = props.product.productVariants.find((productVariant) => {
        return serialize(optionsByGroupName) === productVariant.optionVariant;
    });
    return variant ?? props.product;
});

const totalPrice = computed(() => {
    const basePrice = isRangedPriced.value ? getRangePrice(count.value, props.product) : offering.value.salePrice;
    return (basePrice + totalAddonPrice.value) * count.value;
});

onMounted(() => {
    if (images.value) {
        ((productModalRef.value.$el as HTMLDivElement).children[0] as HTMLDivElement)
            .addEventListener('scroll', handleScroll, { passive: true });
    }
});
</script>
<style lang="scss">
.stepper-style {
  .hds-m-stepper__wrapper {
    background-color: white;
    border-radius: 6.25rem;
    gap: 0;
    width: 8rem;
    height: 1.9rem;
    padding: 0 0.3rem;
    button {
      &:first-child {
        padding-right: 0;
      }
      &:last-child {
        padding-left: 0;
        padding-right: 0;
      }
      height: auto;
    }
    input {
      padding: 0;
      border: 0;
    }
    @media screen and (max-width: 719px) {
      .hds-a-buttonIcon--tall {
        padding: 0;
      }
    }
    .hds-a-buttonIcon--primary {
      background: none !important;
      border: none !important;
    }
    .hds-a-buttonIcon--primary svg {
      color: black !important;
    }
  }
}

.product-modal-padding {
  .hds-o-modal__block .actions {
    padding-top: 0 !important;
  }
}

.product-modal-top-bar {
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 9;
  padding: 12px 16px 12px 16px;
}
</style>